import { ReactElement, useEffect, useCallback, useState } from "react";
import { getTimeString } from "../../utils/time";
import Select from "react-select"; 
import useApiState, { api } from "../../hooks/use-api-state";
import { IAppInfo, IUserInfo } from "../../messages/IAppInfo";
import Loading from "../common/Loading";
import styles from "./AssessmentSelector.module.css";
import "../common/General.module.css";
import SidebarLayout from "../common/SidebarLayout";
import Assessment from "./Assessment";
import { findQuestionIdByLabId, IAssessmentObject, IQuestion, IAssessments, IAssessmentResponse, IAnswersData } from "../../messages/IAssessments";
import { hasPermission } from "../common/ProtectedRoute";

let indexToKey : { [key:number] : string} = {};
let keyToIndex : { [key:string] : number} = {};

const ELAPSED_TEXT_TOP = 10;

export default function AssessmentSelector(props: {
  appInfo: IAppInfo,
  userInfo: IUserInfo,
}): ReactElement {
  //const [searchParams, setSearchParams] = useSearchParams();
  const [getAssessmentListApiState, setGetAssessmentListApiState] = useApiState(false);
  const [getAssessmentsApiState, setGetAssessmentApiState] = useApiState(false);
  const [assessmentsList, setAssessmentsList] = useState<IAssessments>({}); // List of IAssessmentObject
  const [selectedAssessmentObject, setSelectedAssessmentObject] = useState<IAssessmentObject | null>(null);
  //
  const [assessment, setAssessment] = useState<{assessmentObject:IAssessmentObject|null, questions:IQuestion[]|null, answersData:IAnswersData|null}>({assessmentObject:null, questions:null, answersData:null});
  //
  const [readonly, setReadonly] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedAssessmentHover, setSelectedAssessmentHover] = useState<string | null>(null); // Obsolete this
  const [elapsed, setElapsed] = useState<number | null>(null); // In seconds (or null if no counter)
  const [submitting, setSubmitting] = useState(false);

  let fetchCallback = useCallback(async function () {
    setAssessment({assessmentObject:null, questions:null, answersData:null});
    //First, check if we are just showing an assessment (and if so, just load that one)
    const urlMatch = window.location.pathname.match(/^\/showassessment\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)/);
    if (urlMatch) {
      const [, cohort_id, lmodule_code, user_id, question_id] = urlMatch;
      const decodedQuestionId = decodeURIComponent(question_id);
      setReadonly(true);
      showAssessment(cohort_id, lmodule_code, decodedQuestionId, user_id, false);
    }
    else {
      try {
        let ret = await api.getAssessmentsList(setGetAssessmentListApiState);
        setAssessmentsList(ret);
        setAssessment({assessmentObject:null, questions:null, answersData:null});
        setReadonly(false);
      } catch (e: any) {
        console.error("Error fetching assessments list:", e);
      }  
    }
 }, [setGetAssessmentListApiState]);


  useEffect(() => {
    document.title = "Assessments";
    fetchCallback();
    connectToSqs();
  }, [fetchCallback, props.userInfo.user_id, props.appInfo.csrf_token]);


  useEffect(() => {
    const handleScroll = () => {
      const elapsedDivElement = document.getElementById("elapsedDiv");
      const elapsedTextElement = document.getElementById("elapsedText");
      if (elapsedDivElement && elapsedTextElement) {
        const rect = elapsedDivElement.getBoundingClientRect();
        if (rect.top<ELAPSED_TEXT_TOP) {
          elapsedTextElement.style.top = `${ELAPSED_TEXT_TOP}px`;
          elapsedTextElement.style.position = "fixed";
        }
        else {
          elapsedTextElement.style.top = "auto";
          elapsedTextElement.style.position = "static";
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  function connectToSqs() {
    sqsutils.init("", 20, props.appInfo.csrf_token,
      () => {}, // connectedCallback
      (immediateReconnect) => {}, // disconnectedCallback
      () => {}, // statusChangeCallback
      (error)=>{
        setMessage("Error connecting to the server. Please try again later.");
      }, // giveupCallback
      false);
    sqsutils.sqs_connect(props.userInfo.user_id, "ASSESSMENT", false);
  }


  useEffect(() => {
    sqsutils.registerChannelProcessor("ASSESSMENT", (json: SQSMessage) => {
      //console.log(`Received message from channel ASSESSMENT: ${JSON.stringify(json)}`);
      if (json.cmd==="labstarted" || json.cmd==="labfinished") {
        const questionId = findQuestionIdByLabId(assessment.questions, json.labid);
        if (questionId) {
          let _answersData = assessment.answersData;
          if (!_answersData)
            _answersData = { key: "", answers: {}, submitted: false, passed: false, can_restart_at: 0, can_restart_at_text: "", results: {} };
          if (!_answersData.answers)
            _answersData.answers = {};
          _answersData.answers[questionId] = json.cmd==="labstarted" ? { "doing": true } : { "finished": true };
          setAssessment((prev) => ({ ...(prev ?? {}), answersData: _answersData }));
        }
      }
      else if (json.cmd==="message") {
        if (json.message) 
          setMessage(json.message);
      }
      else if (json.cmd==="reload") {
        if (json.message) 
          setMessage(json.message);
        if (json.key && json.qualification_certificate_link) {
          const referredAsssessment = assessmentsList[json.key];
          if (referredAsssessment) {
            referredAsssessment.qualification_certificate_link = json.qualification_certificate_link;
            setAssessment((prev) => ({ ...(prev ?? {}), assessmentObject: referredAsssessment }));
          }
        }
      }
    });
  }, [assessmentsList, assessment.questions]);


  useEffect(() => {
    //console.log(`Assessment changed: object.key=${assessment?.assessmentObject?.key} questions=${assessment?.questions?.map((question: any) => question.id).join(", ") ?? ""} answersData.key=${assessment?.answersData?.key ?? ""}`);
    if (assessment && assessment.answersData && !assessment.answersData.submitted) {
    }
    else {
      setElapsed(null);
    }
  }, [assessment]);


  function asssessmentSelected(selectedOption: any) {
    //let sp = new URLSearchParams(searchParams);
    if (selectedOption.value) {
      const selectedKey = indexToKey[selectedOption.value];
      const newAsssessment = assessmentsList[selectedKey];
      setSelectedAssessmentObject(newAsssessment);

      //console.log("Selected assessment:", newAsssessment);
      //console.log(`submitted=${newAsssessment.submitted} enabled=${newAsssessment.enabled} active=${newAsssessment.active} started_at=${newAsssessment.started_at}`);

      if (hasPermission(props.userInfo, [ { permission: "test" } ]) && newAsssessment.hover) {
        setSelectedAssessmentHover(newAsssessment.hover);
      }

      setElapsed(null);

      if (newAsssessment.submitted)
        setMessage("This assessment has already been submitted.");
      else if (!newAsssessment.enabled && newAsssessment.disable_text)
        setMessage(newAsssessment.disable_text);
      else {
        setMessage(null);
      }
  
      if (!newAsssessment.enabled && !newAsssessment.submitted) {
        setAssessment({assessmentObject:null, questions:null, answersData:null});
      }
      else {
        setAssessment(prev => ({ ...prev, assessmentObject:newAsssessment }));
        if (newAsssessment.submitted || newAsssessment.started_at) {
          showAssessmentObject(newAsssessment, false);
        }
        else {
          setAssessment(prev => ({ ...prev, questions:null, answersData:null }));
        }
      } 
    }
    else {
      setAssessment({assessmentObject:null, questions:null, answersData:null});
      setMessage(null);
      setElapsed(null);
    }
  }


  async function showAssessment(cohort_id:string, lmodule_code:string, questions_id:string, user_id:string|null=null, starting:boolean=false) {
      try {
        let ret = await api.getAssessment(setGetAssessmentApiState, starting, cohort_id, lmodule_code, questions_id, user_id);
        //console.log("Assessment object:", ret.assessment_obj);
        setAssessment({assessmentObject:ret.assessment_obj, questions:ret.questions??null, answersData:ret.answers_data??null});
        setMessage(ret.message??null);
      } catch (e: any) {
        setMessage("Error fetching assessment:");
        //console.error("Error fetching assessment:", e);
      }
  }


  async function showAssessmentObject(_assessmentObject: IAssessmentObject, starting:boolean=false) {
    if (_assessmentObject) {
      showAssessment(_assessmentObject.cohort_id, _assessmentObject.lmodule_code, _assessmentObject.questions_id, null, starting);
    }
  }


  function updateAssessment(submitting:boolean, assessment_obj: IAssessmentObject, answers_data: IAnswersData|null) {
    setSubmitting(submitting);
    if (submitting)
      setElapsed(null);

    setAssessment(prev => ({ ...prev, assessmentObject:assessment_obj, answersData:answers_data }));

    let key = assessment_obj.key;
    setAssessmentsList((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? {}),
        ...assessment_obj,
      },
    }));
  }


  return (
    <>
      <div>
        <SidebarLayout
          keyForStorage={`assessmentSelector`}
          minimizedTitle="Assessments"
          sidebarContainerClass={styles.assessmentSidebar}
          hideSidebar={readonly}
          sidebarContent={
            <Loading isLoading={getAssessmentListApiState.isLoading}>
              <div className={styles.selectorContainer} title={selectedAssessmentHover??""}>
                <h4 className={styles.selectLabel}>Select an assessment</h4>
                <Select
                  className={`generalSelector`}
                  classNamePrefix="generalSelector"
                  options={Object.entries(assessmentsList).map(([key, assessmentObj], index) => {
                    indexToKey[index+1] = key;
                    keyToIndex[key] = index+1;
                    let title = `${assessmentObj.title} ${assessmentObj.multiple_lmodules ? `(${assessmentObj.lmodule_title ?? ""})` : ""}`;
                    return { label: title, value: index+1 };
                  })}
                  onChange={asssessmentSelected}
                  value={ selectedAssessmentObject ? { label: `${selectedAssessmentObject.title} ${selectedAssessmentObject.multiple_lmodules ? `(${selectedAssessmentObject.lmodule_title ?? ""})` : ""}`, 
                          value: keyToIndex[selectedAssessmentObject.key ?? 1] } : ""}
                  defaultValue={{ label: assessmentsList[indexToKey[1]]?.title ?? "", value: 1 }}
                />
                {assessment?.assessmentObject && (
                  elapsed && !submitting ? (
                    <div id="elapsedDiv" className={styles.elapsedIndicator}>
                      <span id="elapsedText">{getTimeString(elapsed)} (max {assessment?.assessmentObject.fillout_time} min)</span>
                    </div>
                  ) : (
                    <>
                    {assessment?.assessmentObject.enabled && !assessment?.assessmentObject.submitted && !submitting && !getAssessmentsApiState.isLoading && (
                      <button
                        className={styles.startButton}
                        onClick={() => { if (assessment?.assessmentObject) showAssessmentObject(assessment?.assessmentObject, true) }}
                        disabled={assessment?.assessmentObject.submitted || !assessment?.assessmentObject.enabled}
                      >Start
                      </button>
                    )}
                    </>
                  )
                )}
              </div>
            </Loading>
          }
          mainContent={
            <>
            {message && (
              <div className={styles.message}>
                {message}
              </div>
            )}
            <Loading isLoading={getAssessmentsApiState.isLoading} className={styles.assessmentLoader}>
              {assessment?.assessmentObject && assessment?.questions ? (
              <Assessment
                anonymous={false}
                appInfo={props.appInfo}
                userInfo={props.userInfo}
                assessmentObject={assessment?.assessmentObject}
                questions={assessment?.questions}
                answersData={assessment?.answersData}
                showOnly={readonly}
                allowFilling={!readonly}
                allowSubmit={!readonly}
                canSave={!readonly}
                recaptchaToken={null}
                setMessage={setMessage}
                setElapsed={(assessment?.assessmentObject.fillout_time??0) > 0 ? setElapsed : undefined}
                updateAssessment={updateAssessment}
              />
              ) : (
                <></>
              )}
            </Loading>
            </>
          }
        />
      </div>
    </>
  );
}
